/**
 * Cookie / tracker consent.
 *
 * ZEKom-2, 225. člen (the Slovenian transposition of the ePrivacy Directive)
 * allows storing or reading information on a user's device only with prior
 * informed consent, except where it is strictly necessary to deliver a service
 * the user explicitly requested. GDPR Art. 6(1)(a) + Art. 7 then govern the
 * quality of that consent: it must be freely given, specific, unambiguous, as
 * easy to withdraw as to give, and it must be OPT-IN — silence, pre-ticked
 * boxes and "continued browsing" are not consent.
 *
 * Consequences for this module:
 *   - Nothing non-essential loads until `analytics` / `marketing` is `true`.
 *     GA4 and the Meta Pixel are therefore NOT in index.html; they are injected
 *     from here, after the visitor has chosen.
 *   - Rejecting must be as cheap as accepting, so the banner has a same-level
 *     "reject" button and this module treats "no stored decision" as "denied".
 *   - The decision is versioned. Bumping CONSENT_VERSION re-asks everyone,
 *     which is what has to happen when the set of trackers materially changes.
 */

export type ConsentCategory = "analytics" | "marketing";

export interface ConsentState {
  version: number;
  /** ISO timestamp — evidence of when consent was given (GDPR Art. 7(1)). */
  timestamp: string;
  analytics: boolean;
  marketing: boolean;
}

/** Bump when the tracker set changes materially; it invalidates stored consent. */
export const CONSENT_VERSION = 1;

const STORAGE_KEY = "nreport-cookie-consent";
/** Fired on the window so the banner and any listener can react to a change. */
const CHANGE_EVENT = "nreport:consent-change";
/** Fired to reopen the preferences dialog from a footer / policy-page link. */
export const OPEN_SETTINGS_EVENT = "nreport:consent-open";

const DENIED: ConsentState = {
  version: CONSENT_VERSION,
  timestamp: "",
  analytics: false,
  marketing: false,
};

const isBrowser = () => typeof window !== "undefined";

/**
 * The stored decision, or `null` when the visitor has not decided yet (or the
 * stored decision predates the current version). `null` means "ask", and until
 * they answer, nothing non-essential runs.
 */
export function getStoredConsent(): ConsentState | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (parsed.version !== CONSENT_VERSION) return null;
    return {
      version: CONSENT_VERSION,
      timestamp: typeof parsed.timestamp === "string" ? parsed.timestamp : "",
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
    };
  } catch {
    return null;
  }
}

/** Effective consent — denied for everything until the visitor opts in. */
export function getConsent(): ConsentState {
  return getStoredConsent() ?? DENIED;
}

export function hasConsent(category: ConsentCategory): boolean {
  return getConsent()[category];
}

/** Persist a decision, apply it to the trackers, and notify listeners. */
export function setConsent(choice: { analytics: boolean; marketing: boolean }): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    analytics: choice.analytics,
    marketing: choice.marketing,
  };
  if (isBrowser()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Private mode / quota. The decision still applies for this page view;
      // without storage the banner simply reappears on the next visit, which is
      // the safe direction to fail in.
    }
    applyConsent(state);
    window.dispatchEvent(new CustomEvent<ConsentState>(CHANGE_EVENT, { detail: state }));
  }
  return state;
}

export function subscribeToConsent(listener: (state: ConsentState) => void): () => void {
  if (!isBrowser()) return () => {};
  const handler = (event: Event) => listener((event as CustomEvent<ConsentState>).detail);
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** Reopen the preference dialog — wired to the footer and the cookie policy. */
export function openCookieSettings() {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

// ---------------------------------------------------------------------------
// Tracker loading
// ---------------------------------------------------------------------------

const GA_MEASUREMENT_ID = "G-WWDRJTW8KX";
const META_PIXEL_ID = "1570650787762842";

let gaLoaded = false;
let metaLoaded = false;

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: GtagFn;
    dataLayer?: unknown[];
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean };
    _fbq?: unknown;
  }
}

/**
 * Install the `gtag` shim and set Consent Mode v2 defaults to denied.
 *
 * Safe to call before any consent exists: with every signal denied, gtag.js
 * (once loaded) sends no cookies and no identifiers. Calling it up front means
 * a later grant is a `consent: update`, which is what Google expects.
 */
function ensureGtagStub() {
  if (typeof window.gtag === "function") return;
  window.dataLayer = window.dataLayer || [];
  // Must push the `arguments` object, exactly like Google's snippet. gtag.js
  // ignores plain arrays in dataLayer, so a `(...args) => push(args)` shim
  // loads the library but never sends a single hit (GA4 was dark Aug–Sep 2026).
  const gtag: GtagFn = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag = gtag;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500,
  });
}

function loadGoogleAnalytics() {
  if (gaLoaded) return;
  gaLoaded = true;
  ensureGtagStub();
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  window.gtag?.("js", new Date());
  window.gtag?.("config", GA_MEASUREMENT_ID, {
    // Belt and braces alongside Consent Mode: no ad-network sync from an
    // analytics-only opt-in.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

function loadMetaPixel() {
  if (metaLoaded) return;
  metaLoaded = true;
  // The official snippet, transcribed. It defines the fbq queue, then loads
  // fbevents.js which drains it.
  const fbq: Window["fbq"] = Object.assign(
    (...args: unknown[]) => {
      const self = window.fbq;
      if (!self) return;
      // fbevents.js swaps in its own callMethod once loaded.
      const withMethod = self as unknown as { callMethod?: (...a: unknown[]) => void };
      if (withMethod.callMethod) withMethod.callMethod(...args);
      else self.queue?.push(args);
    },
    { queue: [] as unknown[], loaded: true, version: "2.0" },
  );
  window.fbq = fbq;
  window._fbq = window._fbq || fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
}

/**
 * Best-effort removal of cookies a tracker already set, for the case where a
 * visitor withdraws consent. The third-party `_fbp`/`_ga` cookies are set on
 * our own domain by JS, so we can expire them; anything set by a third-party
 * host is outside our reach and is covered by the provider's own opt-out.
 */
function clearTrackerCookies(names: string[]) {
  const host = window.location.hostname;
  // Try the exact host and the registrable-domain form the libraries use.
  const domains = [undefined, host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  for (const name of names) {
    for (const domain of domains) {
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
        (domain ? `; domain=${domain}` : "");
    }
  }
}

/**
 * Bring the loaded trackers in line with a consent state.
 *
 * Loading is one-way within a page view — a script cannot be un-injected — so
 * withdrawal updates Consent Mode, expires the identifiers we can reach, and
 * relies on the next page load starting clean. That is the standard, and
 * accepted, behaviour for a consent manager.
 */
export function applyConsent(state: ConsentState) {
  if (!isBrowser()) return;
  ensureGtagStub();

  window.gtag?.("consent", "update", {
    analytics_storage: state.analytics ? "granted" : "denied",
    ad_storage: state.marketing ? "granted" : "denied",
    ad_user_data: state.marketing ? "granted" : "denied",
    ad_personalization: state.marketing ? "granted" : "denied",
  });

  if (state.analytics) loadGoogleAnalytics();
  else clearTrackerCookies(["_ga", `_ga_${GA_MEASUREMENT_ID.replace("G-", "")}`, "_gid", "_gat"]);

  if (state.marketing) loadMetaPixel();
  else clearTrackerCookies(["_fbp", "_fbc"]);
}

/**
 * Called once on client boot. Applies a previously stored decision; when there
 * is none, it only installs the denied Consent Mode defaults and loads nothing.
 */
export function initConsent() {
  if (!isBrowser()) return;
  const stored = getStoredConsent();
  if (stored) applyConsent(stored);
  else ensureGtagStub();
}
